import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  TextField
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function ScheduleView() {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { t } = useLanguage();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [weekStartDate, setWeekStartDate] = useState(null);
  const [selectedWeekStart, setSelectedWeekStart] = useState(null);

  // Helper: Get next Monday from a date (or same date if already Monday)
  const getNextMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? 1 : day === 1 ? 0 : 8 - day;
    d.setDate(d.getDate() + diff);
    return d;
  };

  // Helper: Format week range for display
  const formatWeekRange = (mondayDate) => {
    if (!mondayDate) return '';
    const monday = new Date(mondayDate);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const options = { month: 'short', day: 'numeric' };
    const mondayStr = monday.toLocaleDateString('en-US', options);
    const sundayStr = sunday.toLocaleDateString('en-US', { ...options, year: 'numeric' });
    
    return `Week of ${mondayStr} - ${sundayStr}`;
  };

  // Initialize selectedWeekStart to next Monday on mount
  useEffect(() => {
    setSelectedWeekStart(getNextMonday(new Date()));
  }, []);

  useEffect(() => {
    if (selectedWeekStart) {
      loadSchedule();
    }
  }, [selectedWeekStart]);

  const loadSchedule = async () => {
    if (!selectedWeekStart) return;
    
    setLoading(true);
    setError(null);
    try {
      const weekStartStr = selectedWeekStart.toISOString().split('T')[0];
      const { data } = await axios.get('/api/schedule/current', {
        params: { week_start_date: weekStartStr }
      });
      if (data.success) {
        setSchedule(data.schedule);
        setWeekStartDate(data.week_start_date);
      } else {
        setError(data.message || 'Failed to load schedule');
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load schedule');
    }
    setLoading(false);
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const getShiftColor = (shiftName) => {
    const colors = {
      'Day': '#4CAF50',
      'Evening': '#FF9800', 
      'Night': '#2196F3',
      'Morning': '#9C27B0',
      'Afternoon': '#F44336'
    };
    return colors[shiftName] || '#757575';
  };

  const renderScheduleTable = () => {
    if (!schedule || !schedule.shifts || !schedule.days) {
      return (
        <Alert severity="info">
          {t('scheduleView.noScheduleData')}
        </Alert>
      );
    }

    const { shifts, days, assignments } = schedule;

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>{t('scheduleView.shift')}</TableCell>
              {days.map(day => (
                <TableCell key={day.id} align="center" sx={{ fontWeight: 'bold' }}>
                  {day.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.map(shift => (
              <TableRow key={shift.id}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={shift.name}
                      size="small"
                      sx={{
                        backgroundColor: getShiftColor(shift.name),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                    </Typography>
                  </Box>
                </TableCell>
                {days.map(day => {
                  const assignment = assignments?.find(a => 
                    a.shift_id === shift.id && a.day_id === day.id
                  );
                  const employees = assignment?.employee_list?.split(',').map(e => e.trim()).filter(e => e) || [];
                  
                  return (
                    <TableCell key={`${shift.id}-${day.id}`} align="center">
                      {employees.length > 0 ? (
                        <Box>
                          {employees.map((emp, index) => (
                            <Chip
                              key={index}
                              label={emp.trim()}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                mb: 0.5,
                                display: 'block',
                                fontSize: '0.75rem'
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t('common.unassigned')}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderScheduleSummary = () => {
    if (!schedule) return null;

    const { shifts, days, assignments } = schedule;
    const totalSlots = shifts.length * days.length;
    const assignedSlots = assignments?.filter(a => 
      a.employee_list && a.employee_list.trim()
    ).length || 0;
    const coverage = totalSlots > 0 ? Math.round((assignedSlots / totalSlots) * 100) : 0;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="primary" />
                <Typography variant="h6">{days?.length || 0}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('scheduleView.daysScheduled')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon color="primary" />
                <Typography variant="h6">{shifts?.length || 0}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('scheduleView.shiftsAvailable')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="primary" />
                <Typography variant="h6">{assignedSlots}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('scheduleView.assignedSlots')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" color={coverage >= 80 ? 'success.main' : 'warning.main'}>
                  {coverage}%
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('scheduleView.coverage')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Check authentication
  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('scheduleView.pleaseLogin')}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {t('scheduleView.title')}
            </Typography>
            {selectedWeekStart && (
              <Typography variant="subtitle1" color="text.secondary">
                {formatWeekRange(selectedWeekStart)}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSchedule}
            disabled={loading}
          >
            {t('common.refresh')}
          </Button>
        </Box>

        {/* Week Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 250 }}>
                  <DatePicker
                    label={t('scheduleView.weekStartDate')}
                value={selectedWeekStart}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedWeekStart(getNextMonday(newValue));
                  }
                }}
                shouldDisableDate={(date) => {
                  return date.getDay() !== 1; // Only allow Mondays
                }}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />
              {selectedWeekStart && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {formatWeekRange(selectedWeekStart)}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {renderScheduleSummary()}
          <Divider sx={{ my: 2 }} />
          {renderScheduleTable()}
        </>
      )}
      </Box>
    </LocalizationProvider>
  );
}
